import { useMemo, useState } from "react";
import { ArrowDown, ArrowUp, BarChart3, Landmark, Table2 } from "lucide-react";
import type { LandDataDistrict, LandDataLevel, LandDataSource, LandDataView, LandDataZone } from "@/lib/api";
import { cn } from "@/lib/cn";
import { useEvidence, type EvidenceItem } from "./evidence";
import { Flag, ReadingHint, SectionCard, fmtNum, yearOf } from "./shared";

export type MetricKey = "gdp" | "industrial_output" | "industrial_firms";
type MetricRow = LandDataDistrict | LandDataZone;

export const METRICS: Record<MetricKey, { label: (r?: MetricRow) => string; unit: string; digits: number }> = {
  gdp: { label: () => "GDP", unit: "亿元", digits: 2 },
  industrial_output: {
    label: (r) => `规上工业${r?.industrial_output_kind === "营收" ? "营收" : "产值"}`,
    unit: "亿元",
    digits: 2,
  },
  industrial_firms: { label: () => "规上工业企业", unit: "家", digits: 0 },
};

export function metricYear(row: MetricRow, key: MetricKey): number | null {
  if (key === "gdp") return yearOf(row.gdp_year);
  // 企业数年份单独记录；没有就是来源未标注，不借用产值年份
  if (key === "industrial_firms") return row.industrial_firms_year !== undefined ? yearOf(row.industrial_firms_year) : yearOf(row.industrial_year);
  return yearOf(row.industrial_year);
}

/** 列主年份 = 众数；并列取较新；无年份不参与。 */
export function mainYear(rows: MetricRow[], key: MetricKey): number | null {
  const counts = new Map<number, number>();
  for (const r of rows) {
    if (r[key] == null) continue;
    const y = metricYear(r, key);
    if (y) counts.set(y, (counts.get(y) ?? 0) + 1);
  }
  let best: number | null = null;
  for (const [y, n] of counts) {
    if (best == null || n > (counts.get(best) ?? 0) || (n === counts.get(best) && y > best)) best = y;
  }
  return best;
}

export function metricLevel(row: MetricRow, key: MetricKey): LandDataLevel | null {
  const st = row.status?.[key];
  if (st && st.level !== "fallback") return st;
  if (row[key] == null) {
    const note = key === "gdp" && "gdp_note" in row ? row.gdp_note : "";
    return note ? { level: "unpublished", note } : { level: "pending", note: "未取到数值" };
  }
  return st ? { level: "single", note: st.note } : null;
}

export function metricEvidence(row: MetricRow, key: MetricKey, scope: string): EvidenceItem {
  const meta = METRICS[key];
  const year = metricYear(row, key);
  const basis: string[] = [year ? `${year}年` : "来源未标注年份"];
  if (key === "industrial_output" && row.industrial_output_kind === "营收") {
    basis.push("官方未发布规上工业产值，此处为规上工业营业收入");
  }
  if (key !== "gdp") basis.push("规模以上工业口径");
  const src: LandDataSource | null = row.sources[key];
  const val = row[key];
  return {
    title: `${scope} · ${row.name} · ${meta.label(row)}`,
    value: val == null ? metricLevel(row, key)?.note || "未取到" : fmtNum(val, meta.digits),
    unit: val == null ? undefined : meta.unit,
    level: metricLevel(row, key),
    basis,
    source: src,
  };
}

/** Rank among rows sharing the target's year — never mix years into one ordering. */
export function rankOf(
  rows: MetricRow[],
  key: MetricKey,
  target: MetricRow | undefined,
): { rank: number; n: number; year: number | null; total: number } | null {
  if (!target || target[key] == null) return null;
  const year = metricYear(target, key);
  const peers = rows.filter((r) => r[key] != null && metricYear(r, key) === year);
  const rank = peers.filter((r) => (r[key] as number) > (target[key] as number)).length + 1;
  return { rank, n: peers.length, year, total: rows.length };
}

function isTarget(r: MetricRow): boolean {
  return "is_target" in r && !!r.is_target;
}

/** Value cell: the number; a grey year only when it differs from the column's main year. */
export function MetricCell({
  row,
  metric,
  colYear,
  scope,
}: {
  row: MetricRow;
  metric: MetricKey;
  colYear: number | null;
  scope: string;
}): JSX.Element {
  const evidence = useEvidence();
  const val = row[metric];
  const level = metricLevel(row, metric);
  const year = metricYear(row, metric);
  return (
    <button
      type="button"
      onClick={() => evidence.open(metricEvidence(row, metric, scope))}
      className="inline-flex w-full items-center justify-end gap-1.5 rounded px-1 py-0.5 text-right hover:bg-brand-50"
      title="查看来源与截图"
    >
      {val == null ? (
        <Flag level={level} />
      ) : (
        <>
          {metric === "industrial_output" && row.industrial_output_kind === "营收" ? (
            <span className="text-[11px] text-zinc-400" title="官方未发布产值，此处为营收">
              营收
            </span>
          ) : null}
          {year == null ? (
            <span className="text-[11px] text-zinc-400" title="来源未标注年份">
              年份未注明
            </span>
          ) : colYear && year !== colYear ? (
            <span className="rounded bg-zinc-100 px-1 text-[11px] tabular-nums text-zinc-500" title={`${year}年数据，该列多数为${colYear}年`}>
              {year}
            </span>
          ) : null}
          <span className="font-medium tabular-nums text-zinc-900">{fmtNum(val, METRICS[metric].digits)}</span>
          <Flag level={level} />
        </>
      )}
    </button>
  );
}

/** Horizontal bar ranking; value sits right at the bar's end. */
function BarList({
  rows,
  metric,
  onPick,
}: {
  rows: MetricRow[];
  metric: MetricKey;
  onPick: (row: MetricRow) => void;
}): JSX.Element {
  const withValue = rows.filter((r) => r[metric] != null).sort((a, b) => (b[metric] as number) - (a[metric] as number));
  const missing = rows.filter((r) => r[metric] == null);
  const max = Math.max(1, ...withValue.map((r) => r[metric] as number));
  return (
    <div className="max-w-3xl space-y-1">
      {withValue.map((r) => {
        const target = isTarget(r);
        const pct = ((r[metric] as number) / max) * 100;
        return (
          <button
            key={r.name}
            type="button"
            onClick={() => onPick(r)}
            className="grid w-full grid-cols-[96px_minmax(0,1fr)] items-center gap-2 rounded px-1 py-0.5 text-left text-[12px] hover:bg-brand-50 sm:grid-cols-[128px_minmax(0,1fr)]"
          >
            <span className={cn("truncate", target ? "font-semibold text-brand-900" : "text-zinc-600")}>
              {target ? "★ " : ""}
              {r.name}
            </span>
            <span className="flex items-center gap-2">
              <span
                className={cn("block h-3 shrink-0 rounded-sm", target ? "bg-brand-600" : "bg-brand-200")}
                // 值紧贴条尾：条宽按"扣掉数值位后的可用宽度"缩放
                style={{ width: `calc((100% - 80px) * ${Math.max(pct, 1.5) / 100})` }}
              />
              <span className="shrink-0 tabular-nums text-zinc-800">{fmtNum(r[metric], METRICS[metric].digits)}</span>
            </span>
          </button>
        );
      })}
      {missing.map((r) => (
        <button
          key={r.name}
          type="button"
          onClick={() => onPick(r)}
          className="grid w-full grid-cols-[96px_minmax(0,1fr)] items-center gap-2 rounded px-1 py-0.5 text-left text-[12px] hover:bg-brand-50 sm:grid-cols-[128px_minmax(0,1fr)]"
        >
          <span className="truncate text-zinc-400">{r.name}</span>
          <Flag level={metricLevel(r, metric)} className="text-left" />
        </button>
      ))}
    </div>
  );
}

type SortKey = "name" | MetricKey;

export function MetricCompareSection({
  data,
  kind,
}: {
  data: LandDataView;
  kind: "districts" | "zones";
}): JSX.Element | null {
  const evidence = useEvidence();
  const rows: MetricRow[] = kind === "districts" ? data.districts : data.zones;
  const metrics: MetricKey[] = useMemo(() => {
    const all: MetricKey[] = ["gdp", "industrial_output", "industrial_firms"];
    return kind === "zones" ? all.filter((k) => k !== "gdp" || rows.some((r) => r.gdp != null)) : all;
  }, [kind, rows]);
  const [view, setView] = useState<"table" | "bars">("table");
  const [metric, setMetric] = useState<MetricKey>(metrics[0] ?? "industrial_output");
  const [sort, setSort] = useState<{ key: SortKey; desc: boolean }>({ key: metrics[0] ?? "name", desc: true });

  const colYears = useMemo(() => {
    const out = {} as Record<MetricKey, number | null>;
    for (const k of metrics) out[k] = mainYear(rows, k);
    return out;
  }, [rows, metrics]);

  const sorted = useMemo(() => {
    const list = [...rows];
    list.sort((a, b) => {
      const ta = isTarget(a) ? 1 : 0;
      const tb = isTarget(b) ? 1 : 0;
      if (ta !== tb) return tb - ta; // 本区置顶
      if (sort.key === "name") return sort.desc ? b.name.localeCompare(a.name, "zh") : a.name.localeCompare(b.name, "zh");
      const va = a[sort.key];
      const vb = b[sort.key];
      if (va == null && vb == null) return 0;
      if (va == null) return 1;
      if (vb == null) return -1;
      return sort.desc ? vb - va : va - vb;
    });
    return list;
  }, [rows, sort]);

  if (!rows.length) return null;
  const scope = kind === "districts" ? data.project.city || "区县" : "开发区";
  const onHeader = (key: SortKey): void =>
    setSort((s) => (s.key === key ? { key, desc: !s.desc } : { key, desc: key !== "name" }));
  const headLabel = (k: MetricKey): string =>
    `${METRICS[k].label()}（${METRICS[k].unit}${colYears[k] ? `·${colYears[k]}` : ""}）`;

  return (
    <SectionCard
      icon={<Landmark className="h-4 w-4" />}
      id={kind === "districts" ? "sec-districts" : "sec-zones"}
      title={kind === "districts" ? "区县指标对比" : "开发区 / 经开区"}
      count={`${rows.length} 个`}
      actions={
        <div className="inline-flex rounded-lg border border-cardline bg-white p-0.5">
          {(
            [
              ["table", "表格", Table2],
              ["bars", "条形", BarChart3],
            ] as const
          ).map(([key, label, Icon]) => (
            <button
              key={key}
              type="button"
              onClick={() => setView(key)}
              className={cn(
                "inline-flex items-center gap-1 rounded-md px-2.5 py-1 text-[12px]",
                view === key ? "bg-brand-50 font-medium text-brand-900" : "text-zinc-500 hover:text-brand-800",
              )}
            >
              <Icon className="h-3.5 w-3.5" />
              {label}
            </button>
          ))}
        </div>
      }
    >
      {view === "bars" ? (
        <>
          <div className="mb-3 flex flex-wrap items-center gap-2">
            <div className="inline-flex rounded-lg border border-cardline bg-canvas p-0.5">
              {metrics.map((k) => (
                <button
                  key={k}
                  type="button"
                  onClick={() => setMetric(k)}
                  className={cn(
                    "rounded-md px-3 py-1 text-[12px] font-medium transition-colors",
                    metric === k ? "bg-white text-brand-900 shadow-sm" : "text-zinc-500 hover:text-brand-800",
                  )}
                >
                  {METRICS[k].label()}
                </button>
              ))}
            </div>
            <span className="text-[12px] text-zinc-400">{headLabel(metric).replace(METRICS[metric].label(), "单位")}</span>
          </div>
          <BarList rows={rows} metric={metric} onPick={(r) => evidence.open(metricEvidence(r, metric, scope))} />
        </>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-cardline">
          <table className="w-full min-w-[520px] text-[13px]">
            <thead>
              <tr className="bg-cardhead text-[12px] text-zinc-500">
                <SortTh label={kind === "districts" ? "区县" : "开发区"} k="name" sort={sort} onClick={onHeader} align="left" />
                {metrics.map((k) => (
                  <SortTh key={k} label={headLabel(k)} k={k} sort={sort} onClick={onHeader} />
                ))}
              </tr>
            </thead>
            <tbody>
              {sorted.map((r, i) => {
                const target = isTarget(r);
                return (
                  <tr
                    key={r.name}
                    className={cn("border-t border-cardline/70", target ? "bg-brand-50" : i % 2 ? "bg-canvas/60" : "bg-white")}
                  >
                    <td className={cn("px-3 py-1.5", target ? "font-semibold text-brand-900" : "text-zinc-700")}>
                      {target ? <span className="mr-1 text-brand-600">★</span> : null}
                      {r.name}
                      {target ? <span className="ml-1.5 text-[11px] font-normal text-brand-600">本项目所在</span> : null}
                    </td>
                    {metrics.map((k) => (
                      <td key={k} className="px-2 py-1">
                        <MetricCell row={r} metric={k} colYear={colYears[k]} scope={scope} />
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      <div className="mt-2.5 flex flex-wrap items-center justify-between gap-2">
        <ReadingHint yearTag={view === "table"} />
        {kind === "zones" ? (
          <span className="text-[11px] text-zinc-400">仅列采集库已收录、且本市有数据的开发区；未收录的暂缺（不代表无）。</span>
        ) : null}
      </div>
    </SectionCard>
  );
}

function SortTh({
  label,
  k,
  sort,
  onClick,
  align = "right",
}: {
  label: string;
  k: SortKey;
  sort: { key: SortKey; desc: boolean };
  onClick: (k: SortKey) => void;
  align?: "left" | "right";
}): JSX.Element {
  const active = sort.key === k;
  return (
    <th className={cn("px-3 py-2 font-medium", align === "right" ? "text-right" : "text-left")}>
      <button
        type="button"
        onClick={() => onClick(k)}
        className={cn("inline-flex items-center gap-0.5 hover:text-brand-800", active && "text-brand-900")}
      >
        {label}
        {active ? sort.desc ? <ArrowDown className="h-3 w-3" /> : <ArrowUp className="h-3 w-3" /> : null}
      </button>
    </th>
  );
}
